/**
 * Viewport/Intersection Observer utilities
 * @module lib/viewportObserver
 */

const { logDebug } = require('./logger');

function onElementInViewport(options = {}) {
  if (typeof document === 'undefined') return;
  const selector = options.selector;
  const onIntersect = options.onIntersect;
  if (!selector || typeof onIntersect !== 'function') return;

  const thresholdPx = Number(options.threshold);
  const marginPx = Number.isFinite(thresholdPx) && thresholdPx > 0 ? thresholdPx : 0;
  const rootMargin =
    typeof options.rootMargin === 'string'
      ? options.rootMargin
      : `0px 0px ${marginPx}px 0px`;
  const intersectionThreshold =
    options.intersectionThreshold !== undefined ? options.intersectionThreshold : 0;
  const oneTime = options.oneTime === true;

  function parseRootMargin(value) {
    if (typeof value !== 'string') {
      return { top: 0, right: 0, bottom: 0, left: 0 };
    }
    const parts = value.trim().split(/\s+/);
    const nums = parts.map((part) => {
      const match = /(-?\d+(?:\.\d+)?)px/.exec(part);
      return match ? Number(match[1]) : 0;
    });
    if (nums.length === 1) {
      return { top: nums[0], right: nums[0], bottom: nums[0], left: nums[0] };
    }
    if (nums.length === 2) {
      return { top: nums[0], right: nums[1], bottom: nums[0], left: nums[1] };
    }
    if (nums.length === 3) {
      return { top: nums[0], right: nums[1], bottom: nums[2], left: nums[1] };
    }
    if (nums.length >= 4) {
      return { top: nums[0], right: nums[1], bottom: nums[2], left: nums[3] };
    }
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }

  const rootMarginValues = parseRootMargin(rootMargin);

  const observed = new Set();
  const seen = new Set();
  const zeroAreaCandidates = new Set();
  const zeroAreaInView = new Set();
  let zeroAreaCheckScheduled = false;
  let observer = null;

  function observeElement(element, observer) {
    if (!element || element.nodeType !== 1) return;
    if (seen.has(element) || observed.has(element)) return;
    observer.observe(element);
    observed.add(element);
    logDebug('viewport:observe', { selector, id: element.id || null });
    trackZeroAreaCandidate(element);
  }

  function handleNode(node, observer) {
    if (!node || node.nodeType !== 1) return;
    const element = node;
    if (element.matches && element.matches(selector)) {
      observeElement(element, observer);
    }
    if (element.querySelectorAll) {
      element.querySelectorAll(selector).forEach((child) => observeElement(child, observer));
    }
  }

  function hasLayout(rect) {
    return !(rect.top === 0 && rect.bottom === 0 && rect.left === 0 && rect.right === 0);
  }

  function isZeroAreaRectInViewport(rect) {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const withinVertical =
      rect.bottom >= -rootMarginValues.top &&
      rect.top <= viewportHeight + rootMarginValues.bottom;
    const withinHorizontal =
      rect.right >= -rootMarginValues.left &&
      rect.left <= viewportWidth + rootMarginValues.right;
    return withinVertical && withinHorizontal;
  }

  function trackZeroAreaCandidate(element) {
    if (!element || typeof element.getBoundingClientRect !== 'function') return;
    if (seen.has(element)) return;
    const rect = element.getBoundingClientRect();
    if (!hasLayout(rect)) return;
    if (rect.width > 0 && rect.height > 0) {
      zeroAreaCandidates.delete(element);
      zeroAreaInView.delete(element);
      return;
    }
    zeroAreaCandidates.add(element);
    scheduleZeroAreaCheck();
  }

  function runZeroAreaCheck() {
    if (zeroAreaCandidates.size === 0) return;
    zeroAreaCandidates.forEach((element) => {
      if (!element || !document.contains(element)) {
        zeroAreaCandidates.delete(element);
        zeroAreaInView.delete(element);
        return;
      }
      if (seen.has(element)) {
        zeroAreaCandidates.delete(element);
        zeroAreaInView.delete(element);
        return;
      }
      const rect = element.getBoundingClientRect();
      if (!hasLayout(rect)) {
        zeroAreaInView.delete(element);
        return;
      }
      if (rect.width > 0 && rect.height > 0) {
        zeroAreaCandidates.delete(element);
        zeroAreaInView.delete(element);
        return;
      }
      const isInView = isZeroAreaRectInViewport(rect);
      if (isInView && !zeroAreaInView.has(element)) {
        logDebug('viewport:zero-area-intersect', {
          selector,
          id: element.id || null,
          rect: {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height
          }
        });
        onIntersect({ target: element, isIntersecting: true, intersectionRatio: 1 });
        if (oneTime) {
          seen.add(element);
          zeroAreaCandidates.delete(element);
          zeroAreaInView.delete(element);
          if (observer && typeof observer.unobserve === 'function') {
            observer.unobserve(element);
            observed.delete(element);
          }
        } else {
          zeroAreaInView.add(element);
        }
      } else if (!isInView) {
        zeroAreaInView.delete(element);
      }
    });
  }

  function scheduleZeroAreaCheck() {
    if (zeroAreaCheckScheduled || typeof window === 'undefined') return;
    zeroAreaCheckScheduled = true;
    const raf = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };
    raf(function () {
      zeroAreaCheckScheduled = false;
      runZeroAreaCheck();
    });
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', scheduleZeroAreaCheck, { passive: true });
    window.addEventListener('resize', scheduleZeroAreaCheck);
  }

  if (typeof IntersectionObserver === 'undefined') {
    document.querySelectorAll(selector).forEach((element) => {
      logDebug('viewport:intersect-fallback', { selector, id: element.id || null });
      onIntersect({ target: element, isIntersecting: true, intersectionRatio: 1 });
    });
    return;
  }

  logDebug('viewport:setup', {
    selector,
    rootMargin,
    threshold: intersectionThreshold,
    oneTime
  });

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        logDebug('viewport:intersect', {
          selector,
          id: entry.target && entry.target.id ? entry.target.id : null,
          ratio: entry.intersectionRatio
        });
        onIntersect(entry);
        if (oneTime) {
          seen.add(entry.target);
          observer.unobserve(entry.target);
          observed.delete(entry.target);
        }
      });
    },
    { rootMargin, threshold: intersectionThreshold }
  );

  document.querySelectorAll(selector).forEach((element) => observeElement(element, observer));
  scheduleZeroAreaCheck();

  if (typeof MutationObserver !== 'undefined') {
    const root = document.documentElement || document.body;
    if (root) {
      const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => handleNode(node, observer));
        });
      });
      mutationObserver.observe(root, { childList: true, subtree: true });
    }
  }
}

module.exports = { onElementInViewport };
